import React, { useState, useEffect, useRef, useCallback } from 'react';
import { BarChart3, Loader2, X } from 'lucide-react';
import SurveyStatsCard from './SurveyStatsCard';
import SessionStatsCard from './SessionStatsCard';
import RecipientStatsCard from './RecipientStatsCard';
import ChannelActivityCard from './ChannelActivityCard';
import SessionTimelineCard from './SessionTimelineCard';
import RecipientsByChannelCard from './RecipientsByChannelCard';
import { fetchDashboardStats } from '../../utils/dashboardApi';
import { getOrganization } from '../../utils/accountApi';

// ── Empty state (used when API returns nothing or while loading) ──
const EMPTY_DATA = {
    surveys: { undeployed: 0, deployed: 0, executed: 0 },
    sessions: { total: 0, paused: 0, expired: 0, errored: 0, per_survey: [], timeline: [] },
    recipients: { total: 0, participated: 0, archived: 0, per_survey: [] },
    channels: {},
    recipients_by_channel: [],
    // BUG-107: per-card diagnostic timestamps. Empty string means the
    // underlying source has no rows (or the query couldn't run).
    meta: {
        channels_last_event_at: '',
        recipients_by_channel_last_event_at: '',
        sessions_last_event_at: '',
    },
};

/**
 * DashboardPanel — account status dashboard.
 *
 * Props:
 *   orgId   (optional) — scope to a single org (Console). Omit for all-orgs (SuperPanel).
 *   onDismiss           — callback when user clicks Ok / X / clicks outside to hide the dashboard.
 */
export default function DashboardPanel({ orgId, onDismiss }) {
    const [data, setData] = useState(EMPTY_DATA);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [orgName, setOrgName] = useState('');
    const panelRef = useRef(null);

    // Fetch org name
    useEffect(() => {
        if (!orgId) return;
        getOrganization(orgId)
            .then(org => setOrgName(org?.name || org?.org_name || ''))
            .catch(() => { });
    }, [orgId]);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError(null);

        fetchDashboardStats(orgId)
            .then(stats => {
                if (!cancelled) {
                    setData({
                        surveys: stats.surveys || EMPTY_DATA.surveys,
                        sessions: stats.sessions || EMPTY_DATA.sessions,
                        recipients: stats.recipients || EMPTY_DATA.recipients,
                        channels: stats.channels || EMPTY_DATA.channels,
                        recipients_by_channel: stats.recipients_by_channel || EMPTY_DATA.recipients_by_channel,
                        meta: stats.meta || EMPTY_DATA.meta,
                    });
                }
            })
            .catch(err => {
                if (!cancelled) setError(err.message || 'Failed to load dashboard');
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => { cancelled = true; };
    }, [orgId]);

    // Click-outside dismiss: if user clicks the backdrop area (not the panel), close.
    const handleBackdropClick = useCallback((e) => {
        if (panelRef.current && !panelRef.current.contains(e.target)) {
            onDismiss();
        }
    }, [onDismiss]);

    return (
        <div style={styles.backdrop} onClick={handleBackdropClick}>
            <div ref={panelRef} style={styles.container}>
                {/* Header with X close button */}
                <div style={styles.header}>
                    <h2 style={styles.heading}>
                        <BarChart3 size={20} /> Dashboard
                        {orgId && <span style={styles.orgBadge}>{orgName || orgId}</span>}
                        {!orgId && <span style={styles.orgBadge}>All Organizations</span>}
                    </h2>
                    <button onClick={onDismiss} style={styles.closeButton} title="Close dashboard">
                        <X size={18} />
                    </button>
                </div>

                {loading && (
                    <div style={styles.loadingRow}>
                        <Loader2 size={20} style={{ animation: 'spin 1s linear infinite' }} />
                        <span style={{ marginLeft: 8, color: '#94A3B8' }}>Loading dashboard...</span>
                    </div>
                )}

                {error && (
                    <div style={styles.errorRow}>
                        Dashboard data unavailable: {error}
                    </div>
                )}

                {/* 3x2 dashboard grid */}
                <div style={styles.grid}>
                    <SurveyStatsCard data={data.surveys} />
                    <SessionStatsCard data={data.sessions} lastEventAt={data.meta?.sessions_last_event_at} />
                    <RecipientStatsCard data={data.recipients} />
                    <ChannelActivityCard data={data.channels} lastEventAt={data.meta?.channels_last_event_at} />
                    <SessionTimelineCard data={data.sessions.timeline} />
                    <RecipientsByChannelCard data={data.recipients_by_channel} lastEventAt={data.meta?.recipients_by_channel_last_event_at} />
                </div>


            </div>
        </div>
    );
}

const styles = {
    backdrop: {
        position: 'absolute',
        inset: 0,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-end',
        overflow: 'auto',
        paddingBottom: 20,
        background: 'rgba(0, 0, 0, 0.3)',
        zIndex: 10,
    },
    container: {
        width: '100%',
        maxWidth: 1200,
        padding: 16,
        paddingTop: 12,
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 16,
    },
    heading: {
        margin: 0,
        fontSize: 18,
        fontWeight: 600,
        color: '#FFFFFF',
        display: 'flex',
        alignItems: 'center',
        gap: 10,
    },
    orgBadge: {
        fontSize: 11,
        fontWeight: 500,
        color: '#94A3B8',
        background: 'rgba(255,255,255,0.08)',
        padding: '2px 8px',
        borderRadius: 4,
    },
    closeButton: {
        background: 'rgba(255,255,255,0.08)',
        border: '1px solid rgba(255,255,255,0.12)',
        borderRadius: 6,
        color: '#94A3B8',
        cursor: 'pointer',
        padding: 6,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'background 0.15s, color 0.15s',
    },
    grid: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr 1fr',
        gap: 16,
    },

    loadingRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '12px 0',
        color: '#94A3B8',
        fontSize: 14,
    },
    errorRow: {
        padding: '8px 12px',
        marginBottom: 12,
        background: 'rgba(239, 68, 68, 0.1)',
        border: '1px solid rgba(239, 68, 68, 0.3)',
        borderRadius: 6,
        color: '#FCA5A5',
        fontSize: 13,
    },
};
